import { CustomFile, selectUserFiles } from "../../Helpers/file";
import { CandyThunkAction } from "../Core/Store/CandyReduxTypes";
import {
    ErrorUploadFiles,
    ExcelsToPartialFufsResponse,
    FileProperties,
    ParsedUploadFileProperties,
    RSVFileProperties,
    ServerErrorResponse,
    UploadFilePropertiesResponse,
} from "../../../../Common/CandyApiClient/Types/DraftActions/FileProperties";
import { IterUtils } from "../../../../Common/IterableUtils";
import { IForm } from "../Form/IForm";
import { arrayIsNotEmpty, isNotNullOrUndefined } from "../../../../Common/TypingUtils";
import {
    FufCombineMethod,
    CombineFufResult,
    CombinatorType,
} from "../../../../Common/CandyApiClient/Types/DraftActions/CombineFufResult";
import { ICombineByTransfers } from "../Core/ModalManager/Modals/UploadSection/Types";
import { UploadFileOptions } from "../Externals/FufManager/IFufManager";

export enum UploadedFileStatus {
    Cancel,
    Resolved,
    Unresolved,
}

export interface UploadedFileInfo extends ErrorUploadFiles {
    uploadStatus: UploadedFileStatus;
    fileProperties?: FileProperties;
    fileName?: string;
}

export interface UploadFufByPatchedFiles {
    uploadStatus: UploadedFileStatus;
    fileProperties?: FileProperties;
    fileName?: string;
}
export interface UploadedRSVFileInfo {
    uploadStatus: UploadedFileStatus;
    fileProperties?: RSVFileProperties;
    fileName?: string;
}

export const uploadFileAction: CandyThunkAction<Promise<CustomFile | undefined>> = async (): Promise<
    CustomFile | undefined
> => {
    const files = await selectUserFiles(false);
    if (arrayIsNotEmpty(files)) {
        const file = IterUtils.single(files);
        return file;
    }
    return undefined;
};

export const uploadFilesAction: CandyThunkAction<Promise<Array<CustomFile | undefined>>> = async (): Promise<
    Array<CustomFile | undefined>
> => {
    const files = await selectUserFiles(true);

    return files;
};

export function uploadFufFileAction(
    file: CustomFile | undefined,
    ignoreSizeCheck?: boolean,
    skipUploadFile?: boolean,
    uploadFileOptions?: UploadFileOptions
): CandyThunkAction<Promise<UploadedFileInfo>> {
    return async (_, __, form: IForm): Promise<UploadedFileInfo> => {
        if (file != undefined) {
            const fileProperties: FileProperties | undefined = await form.fufManager.addFile(
                file,
                ignoreSizeCheck,
                skipUploadFile ?? false,
                uploadFileOptions
            );
            if (fileProperties == undefined) {
                return { uploadStatus: UploadedFileStatus.Unresolved, fileName: file.name };
            } else if (fileProperties.errorCode) {
                return {
                    uploadStatus: UploadedFileStatus.Unresolved,
                    fileName: file.name,
                    errorCode: fileProperties?.errorCode,
                    message: fileProperties?.message,
                };
            } else {
                return {
                    uploadStatus: UploadedFileStatus.Resolved,
                    fileProperties: fileProperties,
                    fileName: file.name,
                } as UploadedFileInfo;
            }
        }
        return { uploadStatus: UploadedFileStatus.Cancel };
    };
}

export function uploadFufByFilePropertiesAction(
    driveURLs: string[],
    abortSignal: AbortSignal
): CandyThunkAction<Promise<ParsedUploadFileProperties>> {
    return async (_, __, form: IForm): Promise<ParsedUploadFileProperties> => {
        const fileProperties = await form.candyApiDraftClient.uploadAndGetFileProperties(
            driveURLs,
            form.settings.gfv,
            abortSignal
        );
        const parsedFileProperties = parseFileProperties(fileProperties);
        return parsedFileProperties;
    };
}

export function combineMultipleFufWithTransferType(
    files: ICombineByTransfers[],
    abortSignal?: AbortSignal
): CandyThunkAction<Promise<ServerErrorResponse | undefined>> {
    return async (_, __, form: IForm): Promise<ServerErrorResponse | undefined> => {
        const fileProperties = await form.candyApiDraftClient.acceptMultipleTransferType(files, abortSignal);
        return fileProperties;
    };
}

function parseFileProperties(fileProperties: UploadFilePropertiesResponse): ParsedUploadFileProperties {
    return {
        sourcesTotalCount: fileProperties.sourcesTotalCount,
        invalidSourceDriveContentNames: fileProperties.invalidSourceDriveContentNames
            ? JSON.parse(fileProperties.invalidSourceDriveContentNames)
            : [],
        fufUri: fileProperties.fufUri,
        prevDocsInstancesCount: Number(fileProperties.prevDocsInstancesCount),
        serverError: fileProperties.serverError,
        gfv: fileProperties.gfv ?? "",
    };
}

export function prepareAndUploadFufFileAction(
    file: CustomFile | undefined,
    gfv: string
): CandyThunkAction<Promise<UploadedFileInfo>> {
    return async (_, __, form: IForm): Promise<UploadedFileInfo> => {
        if (file != undefined) {
            const fileProperties = await form.fufManager.prepareAndAddFile(file, gfv);
            if (fileProperties == undefined) {
                return { uploadStatus: UploadedFileStatus.Unresolved, fileName: file.name };
            } else {
                return {
                    uploadStatus: UploadedFileStatus.Resolved,
                    fileProperties: fileProperties,
                    fileName: file.name,
                };
            }
        }
        return { uploadStatus: UploadedFileStatus.Cancel };
    };
}

export const getFufExpectedDemandFileName: CandyThunkAction<Promise<string | undefined>> = async (
    _,
    __,
    form: IForm
): Promise<string | undefined> => await form.fufManager.getFufExpectedDemandFileName();

export const reloadForm = (navigationPath?: string): void => {
    if (navigationPath && typeof navigationPath === "string") {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("navigationPath", navigationPath);
        window.location.replace(currentUrl.toString());
    } else {
        window.location.reload();
    }
};

export const reloadFormInner =
    (): CandyThunkAction<Promise<void>> =>
    async (_, __, form): Promise<void> => {
        await form.reloadInner();
    };

export function setFufAction(driveUri: string): CandyThunkAction<Promise<void>> {
    return async (_, __, form) => {
        await form.fufManager.setFuf(driveUri);
    };
}

export function excelsToPartialFufsAction(
    gfv: string,
    pathToTable: string,
    driveUris: string[]
): CandyThunkAction<Promise<ExcelsToPartialFufsResponse | undefined>> {
    return async (_, __, form) => await form.fufManager.excelsToPartialFufs(gfv, pathToTable, driveUris);
}

export function importXmlsAction(
    driveUris: string[],
    combineMethod: FufCombineMethod,
    combinatorType: CombinatorType,
    isImportByFuf: boolean,
    args?: string[]
): CandyThunkAction<Promise<CombineFufResult | undefined>> {
    return async (_, __, form) =>
        await form.fufManager.importXmls(driveUris, combineMethod, combinatorType, isImportByFuf, args);
}

export function printExcelAction(pathToTable: string): CandyThunkAction<Promise<boolean>> {
    return async (_, __, form: IForm): Promise<boolean> => {
        const response = await form.candyApiDraftClient.printExcel(pathToTable);
        if (isNotNullOrUndefined(response)) {
            const tableLink = response.resultUri;
            window.open(tableLink, "_blank", "noopener,noreferrer");
        }
        return isNotNullOrUndefined(response);
    };
}
